/**
 * Connections, as a searchable table.
 *
 * Same shape as the Brain and Objects indexes: one row per thing, a search over
 * them, and a ⋯ menu per row. Disconnecting is in that menu rather than only on
 * the detail page — removing something should not require opening it first.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useConnections } from '@/core/connection/connection.context'
import { connectionsApi } from '@/core/api/connections.api'
import { useConnectors } from '@/core/hooks'
import { invalidateConnections } from '@/lib/query.client'
import {
  EmptyState,
  ErrorNotice,
  Loading,
  Logo,
  Modal,
  StatusBadge,
  relativeTime,
} from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { RowMenu, TableSearch } from '@/features/brain/components/RowMenu'
import type { Connection } from '@/core/types/api.types'
import { Constellation } from '@/shared/components/Constellation'

export function ConnectionsPage() {
  const { connections, isLoading, error, refresh } = useConnections()
  const catalog = useConnectors()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [workspace, setWorkspace] = useState('')
  const [tag, setTag] = useState('')
  const [disconnecting, setDisconnecting] = useState<Connection | null>(null)

  // The filter options come from the connections themselves rather than a
  // fixed list, so a workspace that exists is always selectable and one that
  // does not never appears.
  const workspaces = useMemo(
    () => [...new Set(connections.map((c) => c.workspace_id).filter(Boolean))].sort() as string[],
    [connections],
  )
  const tags = useMemo(
    () =>
      [...new Set(connections.flatMap((c) => c.tags ?? []))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [connections],
  )

  const unhealthy = connections.filter((c) => c.status !== 'active')

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return connections.filter((c) => {
      if (workspace && (c.workspace_id ?? '') !== workspace) return false
      if (tag && !(c.tags ?? []).some((t) => t.toLowerCase() === tag.toLowerCase())) return false
      if (!needle) return true
      return (
        c.label.toLowerCase().includes(needle) ||
        c.connector_id.toLowerCase().includes(needle) ||
        c.connection_id.toLowerCase().includes(needle) ||
        (c.tags ?? []).some((t) => t.toLowerCase().includes(needle))
      )
    })
  }, [connections, search, workspace, tag])

  const logoFor = (connectorId: string) =>
    catalog.data?.connectors.find((c) => c.id === connectorId)?.logo ?? null

  return (
    <div className="page" style={{ maxWidth: 1180 }}>
      <div className="page-header hero">
        <Constellation />
        <div className="page-header-row">
          <div>
            <h1>Connections</h1>
            <p>
              Each connection is an id plus an encrypted credential. Every module downstream holds
              only the id.
            </p>
          </div>
          <div className="row">
            <button className="btn" onClick={() => refresh()}>
              Refresh
            </button>
            <Link className="btn btn-primary" to="/catalog">
              Connect an app
            </Link>
          </div>
        </div>
      </div>

      {isLoading && <Loading label="Loading connections…" />}
      {/* Ternary rather than `&&`: `error` is `unknown`, which is not a ReactNode. */}
      {error ? <ErrorNotice error={error} onRetry={() => refresh()} /> : null}

      {!isLoading && connections.length === 0 && (
        <EmptyState
          title="Nothing connected yet"
          action={
            <Link className="btn btn-primary" to="/catalog">
              Browse the catalog
            </Link>
          }
        >
          Connect an app to get a connection id you can run actions against. Every app offers
          several ways in — pick whichever one you already have credentials for.
        </EmptyState>
      )}

      {unhealthy.length > 0 && (
        <div className="callout callout-warn" style={{ marginBottom: 14 }}>
          <p className="callout-title">
            {unhealthy.length} connection{unhealthy.length === 1 ? '' : 's'} need attention
          </p>
          <p className="small">
            {unhealthy.map((c) => c.label).join(', ')} — reconnect to restore access.
          </p>
        </div>
      )}

      {connections.length > 0 && (
        <>
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder="Search connections by name, app, id or tag"
            count={`${rows.length} of ${connections.length}`}
          />

          {(workspaces.length > 0 || tags.length > 0) && (
            <div className="row-tight" style={{ margin: '0 0 12px', flexWrap: 'wrap' }}>
              {workspaces.length > 0 && (
                <select
                  className="select"
                  style={{ width: 'auto' }}
                  value={workspace}
                  aria-label="Filter by workspace"
                  onChange={(e) => setWorkspace(e.target.value)}
                >
                  <option value="">All workspaces</option>
                  {workspaces.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              )}
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  className="chip-filter"
                  data-on={tag === t}
                  onClick={() => setTag((current) => (current === t ? '' : t))}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {connections.length > 0 && rows.length === 0 && (
        <EmptyState title="Nothing matches that search">
          Try a different term, or clear the box.
        </EmptyState>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Connection</th>
                <th>App</th>
                <th>Workspace</th>
                <th>Tags</th>
                <th>Status</th>
                <th>Scopes</th>
                <th>Connected</th>
                <th aria-label="Actions" style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((connection) => (
                <tr
                  key={connection.connection_id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/connections/${connection.connection_id}`)}
                >
                  <td>
                    <span className="row-tight">
                      <Logo
                        src={logoFor(connection.connector_id)}
                        name={connection.label}
                        id={connection.connector_id}
                        small
                      />
                      <strong>{connection.label}</strong>
                    </span>
                    <div className="small faint mono">{connection.connection_id}</div>
                  </td>
                  <td className="mono small">{connection.connector_id}</td>
                  <td className="small muted">{connection.workspace_id || '—'}</td>
                  <td>
                    {connection.tags?.length ? (
                      <span className="chiprow">
                        {connection.tags.map((t) => (
                          <span key={t} className="badge badge-neutral">
                            {t}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td>
                    <StatusBadge status={connection.status} />
                  </td>
                  <td className="small muted">
                    {connection.scopes.length ? `${connection.scopes.length} granted` : '—'}
                  </td>
                  <td className="muted small">{relativeTime(connection.created_at)}</td>
                  <td>
                    <RowMenu
                      label={`Actions for ${connection.label}`}
                      items={[
                        {
                          label: 'Open',
                          onSelect: () => navigate(`/connections/${connection.connection_id}`),
                        },
                        {
                          label: 'Run an action',
                          onSelect: () =>
                            navigate(`/run?connection=${connection.connection_id}`),
                        },
                        {
                          label: 'Disconnect',
                          danger: true,
                          hint: 'deletes its credential',
                          onSelect: () => setDisconnecting(connection),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {disconnecting && (
        <DisconnectDialog
          connection={disconnecting}
          onClose={() => setDisconnecting(null)}
          onDone={() => {
            setDisconnecting(null)
            void refresh()
          }}
        />
      )}
    </div>
  )
}

function DisconnectDialog({
  connection,
  onClose,
  onDone,
}: {
  connection: Connection
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const revoke = useMutation({
    mutationFn: () => connectionsApi.revoke(connection.connection_id),
    onSuccess: async () => {
      await invalidateConnections()
    },
  })

  return (
    <Modal
      title="Disconnect this app"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={revoke.isPending}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            disabled={revoke.isPending}
            onClick={async () => {
              try {
                await revoke.mutateAsync()
                toast.success(`Disconnected ${connection.label}.`)
                onDone()
              } catch (error) {
                toast.error(error)
              }
            }}
          >
            {revoke.isPending ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          Disconnect <strong>{connection.label}</strong>? Its stored credential is deleted and
          cannot be recovered — reconnecting means signing in again.
        </p>
        <p className="muted small" style={{ margin: 0 }}>
          Anything already ingested from it stays where it is. Graphs reading from this connection
          simply stop receiving new rows, and say so on their Sources tab.
        </p>
      </div>
    </Modal>
  )
}
